{
  "$schema": "./brand.schema.json",
  "$comment": "Single source of truth for brand identity (everything a rebrand edits, except the logo SVGs in assets/brand/ and the OAuth signing keypair in oauth.public-jwks.json). Consumed by the app via the typed adapters src/config/brand.ts (identity/links/services), brand-theme.ts (colors) and oauth.ts (oauth), and directly by the Node build scripts, app.config.js and the Cloudflare Workers. Field docs live in brand.schema.json.",
  "name": "mu",
  "hosts": ["mu.social"],
  "socialHandle": "",
  "colors": {
    "neutral": {
      "contrast_0": "#FFFFFF",
      "contrast_25": "#F7F7F2",
      "contrast_50": "#EDEBE5",
      "contrast_100": "#E0DED9",
      "contrast_200": "#C5C4BF",
      "contrast_300": "#ABAAA6",
      "contrast_400": "#92918E",
      "contrast_500": "#797876",
      "contrast_600": "#62615F",
      "contrast_700": "#4B4A49",
      "contrast_800": "#353535",
      "contrast_900": "#2A2A2A",
      "contrast_950": "#212121",
      "contrast_975": "#141414",
      "contrast_1000": "#000000"
    },
    "neutralSubduedOverrides": {
      "contrast_900": "#242424",
      "contrast_950": "#1F1F1F",
      "contrast_975": "#1C1C1C",
      "contrast_1000": "#1A1A1A"
    },
    "accents": {
      "red": {
        "primary_25": "#FFF6F5",
        "primary_50": "#FFE9E6",
        "primary_100": "#FFD2CC",
        "primary_200": "#FFB5AC",
        "primary_300": "#FF9085",
        "primary_400": "#FE655A",
        "primary_500": "#D01D21",
        "primary_600": "#B71A1D",
        "primary_700": "#9A1316",
        "primary_800": "#7D1011",
        "primary_900": "#610C0C",
        "primary_950": "#470908",
        "primary_975": "#2E0505"
      },
      "orange": {
        "primary_25": "#FFF6F3",
        "primary_50": "#FFE9E0",
        "primary_100": "#FED4C1",
        "primary_200": "#FDB89A",
        "primary_300": "#F6996F",
        "primary_400": "#EE783E",
        "primary_500": "#B84B00",
        "primary_600": "#A24100",
        "primary_700": "#883500",
        "primary_800": "#6F2A00",
        "primary_900": "#571F00",
        "primary_950": "#401500",
        "primary_975": "#2A0B00"
      },
      "yellow": {
        "primary_25": "#FDF7F1",
        "primary_50": "#F8ECE0",
        "primary_100": "#EFDAC3",
        "primary_200": "#E6C49D",
        "primary_300": "#DAAA73",
        "primary_400": "#CD9145",
        "primary_500": "#9D6300",
        "primary_600": "#8A5600",
        "primary_700": "#734700",
        "primary_800": "#5E3900",
        "primary_900": "#492B00",
        "primary_950": "#351E00",
        "primary_975": "#221201"
      },
      "green": {
        "primary_25": "#F3FBF5",
        "primary_50": "#E4F3E7",
        "primary_100": "#CAE7D1",
        "primary_200": "#A8D8B5",
        "primary_300": "#82C797",
        "primary_400": "#59B578",
        "primary_500": "#068848",
        "primary_600": "#08773F",
        "primary_700": "#036333",
        "primary_800": "#055028",
        "primary_900": "#053E1E",
        "primary_950": "#042C15",
        "primary_975": "#031C0C"
      },
      "teal": {
        "primary_25": "#F3FAF9",
        "primary_50": "#E3F2F0",
        "primary_100": "#C8E5E1",
        "primary_200": "#A4D6CF",
        "primary_300": "#7CC4BB",
        "primary_400": "#4EB2A7",
        "primary_500": "#00837A",
        "primary_600": "#00736B",
        "primary_700": "#006059",
        "primary_800": "#004E48",
        "primary_900": "#003C37",
        "primary_950": "#002B27",
        "primary_975": "#011B18"
      },
      "cyan": {
        "primary_25": "#F2FAFD",
        "primary_50": "#E1F2F8",
        "primary_100": "#C5E4F0",
        "primary_200": "#A0D4E7",
        "primary_300": "#75C0DB",
        "primary_400": "#42ADCF",
        "primary_500": "#007E9D",
        "primary_600": "#006E8A",
        "primary_700": "#005C73",
        "primary_800": "#004A5E",
        "primary_900": "#003949",
        "primary_950": "#002935",
        "primary_975": "#001922"
      },
      "blue": {
        "primary_25": "#F5F9FF",
        "primary_50": "#E6EFFF",
        "primary_100": "#CDDFFF",
        "primary_200": "#B0CBFF",
        "primary_300": "#8DB4FF",
        "primary_400": "#699BFF",
        "primary_500": "#2764EC",
        "primary_600": "#2258CF",
        "primary_700": "#1A48AF",
        "primary_800": "#143A8E",
        "primary_900": "#0F2D6F",
        "primary_950": "#092051",
        "primary_975": "#051335"
      },
      "indigo": {
        "primary_25": "#F7F8FF",
        "primary_50": "#EAEDFF",
        "primary_100": "#D5DCFF",
        "primary_200": "#BDC6FF",
        "primary_300": "#A1ADFF",
        "primary_400": "#8792FF",
        "primary_500": "#5954F3",
        "primary_600": "#4D49D5",
        "primary_700": "#403CB4",
        "primary_800": "#333093",
        "primary_900": "#262572",
        "primary_950": "#1A1A53",
        "primary_975": "#0F1036"
      },
      "purple": {
        "primary_25": "#FAF7FF",
        "primary_50": "#F3EAFD",
        "primary_100": "#E6D6FA",
        "primary_200": "#D8BDF7",
        "primary_300": "#C7A0F0",
        "primary_400": "#B683E9",
        "primary_500": "#8B50BE",
        "primary_600": "#7946A7",
        "primary_700": "#65398D",
        "primary_800": "#522E72",
        "primary_900": "#3F2358",
        "primary_950": "#2D1840",
        "primary_975": "#1C0E29"
      },
      "pink": {
        "primary_25": "#FFF5FA",
        "primary_50": "#FFE7F3",
        "primary_100": "#FCCFE7",
        "primary_200": "#FAB0D9",
        "primary_300": "#F38EC9",
        "primary_400": "#E969B8",
        "primary_500": "#BB2D8C",
        "primary_600": "#A4277A",
        "primary_700": "#8A1E66",
        "primary_800": "#701852",
        "primary_900": "#571240",
        "primary_950": "#3F0C2E",
        "primary_975": "#29071D"
      }
    },
    "defaultAccent": "pink"
  },
  "oauth": {
    "$comment": "scope = what NEW logins request (keep transitional-only so every PDS accepts it). declaredScope = superset advertised in client metadata (must cover step-ups like identity:handle AND any scope older cached bundles still request). See src/config/oauth.ts.",
    "defaultBaseUrl": "https://mu.social",
    "clientName": "mu",
    "scope": "atproto transition:generic transition:email transition:chat.bsky",
    "declaredScope": "atproto transition:generic transition:email transition:chat.bsky identity:handle account:email?action=manage account:status?action=manage",
    "handleResolver": "https://bsky.social",
    "signupPdsHost": "https://eurosky.social"
  },
  "links": {
    "tos": "https://hello.mu.social/terms",
    "privacy": "https://hello.mu.social/privacy",
    "community": "https://hello.mu.social/guidelines",
    "copyright": "https://hello.mu.social/copyright",
    "support": "https://hello.mu.social/contact",
    "faq": "https://bsky.social/about/faq",
    "dataPrivacy": "https://blueskyweb.zendesk.com/hc/en-us/articles/15835264007693-Data-Privacy",
    "helpDesk": "https://hello.mu.social/contact",
    "supportRequest": "https://hello.mu.social/contact",
    "download": "https://bsky.app/download",
    "statusPage": "https://status.bsky.app/",
    "donate": "https://whydonate.com/fundraising/the-next-era-of-social-media",
    "feedback": "https://userinput.app/s/did:plc:ooensn4mr5mhznzypvxelfa3/3mnwmwn7sn22u"
  },
  "services": {
    "pds": "https://bsky.social",
    "pdsDid": "did:web:bsky.social",
    "publicApi": "https://public.api.bsky.app",
    "appView": "https://api.bsky.app",
    "appViewDid": "did:web:api.bsky.app",
    "chat": "https://api.bsky.chat",
    "embed": "https://embed.bsky.app",
    "gif": "https://gifs.bsky.app",
    "video": "https://video.bsky.app",
    "videoDid": "did:web:video.bsky.app",
    "cdn": "https://cdn.bsky.app",
    "ogCard": "https://ogcard.cdn.bsky.app"
  },
  "verification": {
    "trustedVerifierListUris": [
      "at://did:plc:ooensn4mr5mhznzypvxelfa3/app.bsky.graph.list/3mogaw2g4an23"
    ],
    "includeBlueskyVerifications": true
  },
  "ageAssurance": {
    "serviceUrl": "https://age.mu.social",
    "serviceDid": "did:web:age.mu.social"
  },
  "decorations": {
    "$comment": "Eurosky+ profile decorations. Set enabled=false to remove the UI and read path completely. subscriberListUris are exact app.bsky.graph.list records whose members get access. Test builds override these values with EXPO_PUBLIC_DECO_* variables.",
    "enabled": false,
    "subscriberListUris": [],
    "serviceUrl": "https://deco.mu.social",
    "serviceDid": "did:web:deco.mu.social",
    "manageUrl": "https://mu.social/settings/decorations"
  },
  "fu": {
    "pickerDid": "did:plc:qycn7lwmbl5ije4jkvwksgm3"
  }
}
